'use client';

import { useEffect, useMemo, useRef, useState, type ReactElement } from 'react';
import { TbArrowDown, TbArrowUp } from 'react-icons/tb';

import { cn } from '../lib/cn';

export type KpiVariant = 'positive' | 'negative' | 'neutral';

export interface KpiStatsCardProps {
  label: string;
  value: string;
  deltaPct: number | null;
  sparklineData: number[];
  /**
   * Escala absoluta (em valor) pro eixo Y do sparkline. Quando informada, a
   * altura de cada ponto é proporcional ao VALOR real (ancorada nesse intervalo),
   * NÃO normalizada pelo min/max da própria série. É isso que faz dois cards
   * (ex: Faturamento e Lucro) DIVERGIREM visualmente em vez de virarem cópias
   * afins um do outro. Sem ela, cai no min/max da própria série.
   */
  scaleMin?: number;
  scaleMax?: number;
  /** Cor do VALOR + sparkline (independente do delta). positive=verde, negative=vermelho, neutral=violet/ink. */
  variant: KpiVariant;
  /** Mostra ou não a linha de delta percentual. Default true. */
  showDelta?: boolean;
  /** Quando true, o delta % colore por sinal (verde sobe / vermelho desce) + seta. Default false (cinza). */
  coloredDelta?: boolean;
  /** Linha curta abaixo do valor (ex: "≈ R$ 2.750,00 · cotação do período"). */
  subtitle?: string;
  className?: string;
}

const SVG_W = 220;
const SVG_H = 88;

/**
 * Smooth path com curvas Bezier C — source ravikatiyar 21st.dev.
 *
 * A altura de cada ponto reflete o VALOR real do bucket (não o delta % vs período
 * anterior). Quando `scale` é informada, usa esse intervalo absoluto (ancorado em
 * 0) em vez do min/max da própria série — é o que faz Faturamento e Lucro
 * divergirem visualmente em vez de virarem a mesma curva normalizada.
 * Série plana (ou sem variação) vira uma linha reta na altura do valor.
 */
function generateSmoothPath(
  points: number[],
  width: number,
  height: number,
  scale?: { min: number; max: number },
): string {
  if (!points || points.length < 2) {
    const mid = height / 2;
    return `M 0 ${mid} L ${width} ${mid}`;
  }
  const lo = scale ? scale.min : Math.min(...points);
  const hi = scale ? scale.max : Math.max(...points);
  const range = hi - lo;
  const xStep = width / (points.length - 1);
  const pad = height * 0.18;
  const usable = height - pad * 2;

  const coords: Array<[number, number]> = points.map((p, i) => {
    const x = i * xStep;
    const clamped = p < lo ? lo : p > hi ? hi : p;
    const ratio = range > 0 ? (clamped - lo) / range : 0.5;
    const y = height - pad - ratio * usable;
    return [x, y];
  });

  let path = `M ${coords[0]![0]} ${coords[0]![1]}`;
  for (let i = 0; i < coords.length - 1; i++) {
    const [x1, y1] = coords[i]!;
    const [x2, y2] = coords[i + 1]!;
    const midX = (x1 + x2) / 2;
    path += ` C ${midX},${y1} ${midX},${y2} ${x2},${y2}`;
  }
  return path;
}

function formatDelta(pct: number): string {
  return `${Math.abs(pct).toFixed(1).replace('.', ',')}%`;
}

export function KpiStatsCard({
  label,
  value,
  deltaPct,
  sparklineData,
  scaleMin,
  scaleMax,
  variant,
  showDelta = true,
  coloredDelta = false,
  subtitle,
  className,
}: KpiStatsCardProps): ReactElement {
  const isPositive = deltaPct !== null && deltaPct > 0;
  const isNegative = deltaPct !== null && deltaPct < 0;

  // Cor do VALOR + sparkline = sinal do próprio valor (variant). Independente do delta.
  const stroke =
    variant === 'negative' ? '#f87171' : variant === 'positive' ? '#22c55e' : '#a855f7';

  const valueColorClass =
    variant === 'negative' ? 'text-error' : variant === 'positive' ? 'text-success' : 'text-ink';

  // Cor do DELTA = sinal do delta (só quando coloredDelta).
  const deltaColorClass =
    coloredDelta && deltaPct !== null && deltaPct !== 0
      ? isPositive
        ? 'text-success'
        : 'text-error'
      : 'text-ink-muted';

  // SVG path memoization
  const linePathRef = useRef<SVGPathElement>(null);
  const areaPathRef = useRef<SVGPathElement>(null);
  const scale =
    scaleMin !== undefined && scaleMax !== undefined ? { min: scaleMin, max: scaleMax } : undefined;
  const linePath = useMemo(
    () => generateSmoothPath(sparklineData, SVG_W, SVG_H, scale),
    [sparklineData, scaleMin, scaleMax],
  );
  const areaPath = useMemo(() => {
    if (!linePath.startsWith('M')) return '';
    return `${linePath} L ${SVG_W} ${SVG_H} L 0 ${SVG_H} Z`;
  }, [linePath]);

  // Animação de stroke-dashoffset igual ao ravikatiyar
  const [mounted, setMounted] = useState(false);
  useEffect(() => setMounted(true), []);
  useEffect(() => {
    if (!mounted) return;
    const path = linePathRef.current;
    const area = areaPathRef.current;
    if (!path || !area) return;
    if (typeof path.getTotalLength !== 'function') return; // jsdom não implementa SVG geometry
    const length = path.getTotalLength();
    path.style.transition = 'none';
    path.style.strokeDasharray = `${length} ${length}`;
    path.style.strokeDashoffset = `${length}`;
    area.style.transition = 'none';
    area.style.opacity = '0';
    path.getBoundingClientRect(); // force reflow
    path.style.transition = 'stroke-dashoffset 0.8s ease-in-out, stroke 0.5s ease';
    path.style.strokeDashoffset = '0';
    area.style.transition = 'opacity 0.8s ease-in-out 0.2s, fill 0.5s ease';
    area.style.opacity = '1';
  }, [linePath, mounted]);

  const gradientId = `spark-${variant}-${coloredDelta ? 'c' : 'n'}-${label.replace(/\s+/g, '-').replace(/[^a-zA-Z0-9-]/g, '')}`;

  const hasData = sparklineData.length >= 2;

  // Tamanho do valor por comprimento, pra valores grandes (dezenas de milhar,
  // milhões) não estourarem nem encostarem no sparkline.
  // - Mobile (< sm): valor ocupa a largura toda do card (sparkline some) → escala folgada.
  // - Desktop (sm+): valor divide o card com o sparkline (metade) → escala mais agressiva.
  const valueSizeClass =
    value.length <= 12 ? 'text-4xl' : value.length <= 15 ? 'text-3xl' : 'text-2xl';
  const valueSizeClassDesktop =
    value.length <= 9
      ? 'sm:text-4xl'
      : value.length <= 13
        ? 'sm:text-3xl'
        : value.length <= 17
          ? 'sm:text-2xl'
          : 'sm:text-xl';

  return (
    <article
      className={cn(
        'bg-surface/60 hover:bg-surface group border-border-subtle/30 relative flex min-h-[140px] items-center justify-between gap-4 overflow-hidden rounded-3xl border px-6 py-5 backdrop-blur-xl transition-all duration-300',
        className,
      )}
    >
      <div className="relative flex w-full min-w-0 flex-col sm:w-1/2">
        <div className="text-ink-muted flex items-center gap-2 text-sm font-medium">
          <span className="truncate">{label}</span>
          {showDelta ? (
            deltaPct !== null && deltaPct !== 0 ? (
              <span
                className={cn(
                  'flex items-center gap-0.5 text-[13px] font-semibold',
                  deltaColorClass,
                )}
              >
                {formatDelta(deltaPct)}
                {isPositive ? (
                  <TbArrowUp size={13} />
                ) : isNegative ? (
                  <TbArrowDown size={13} />
                ) : null}
              </span>
            ) : (
              <span className="text-ink-muted text-[13px] font-semibold">—</span>
            )
          ) : null}
        </div>
        <p
          className={cn(
            'mt-2 leading-none font-bold tracking-tight whitespace-nowrap',
            valueSizeClass,
            valueSizeClassDesktop,
            valueColorClass,
          )}
        >
          {value}
        </p>
        {subtitle ? <p className="text-ink-muted mt-2 truncate text-xs">{subtitle}</p> : null}
      </div>

      <div className="relative hidden h-[88px] w-1/2 shrink-0 sm:block">
        <svg
          viewBox={`0 0 ${SVG_W} ${SVG_H}`}
          className="h-full w-full"
          preserveAspectRatio="none"
          aria-hidden
        >
          <defs>
            <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={stroke} stopOpacity={0.4} />
              <stop offset="100%" stopColor={stroke} stopOpacity={0} />
            </linearGradient>
          </defs>
          {hasData ? <path ref={areaPathRef} d={areaPath} fill={`url(#${gradientId})`} /> : null}
          {hasData ? (
            <path
              ref={linePathRef}
              d={linePath}
              fill="none"
              stroke={stroke}
              strokeWidth="3"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          ) : null}
        </svg>
      </div>
    </article>
  );
}
